import { Button, cn, Icon, Text } from '@mntn/ui-components'
import { useState } from 'react'
import { AllocationBreakdown } from './allocation-breakdown'
import { AllocationStatusBadge } from './allocation-status-badge'
import { formatMoney, getAllocationChecks } from './utils'

type Layout = 'panel' | 'inline'

interface AllocationNotifyProps {
  effectiveBudget: number
  ineffectiveBudget: number
  unallocatedBudget: number
  inverse?: boolean
  layout?: Layout
  /**
   * When provided in the inline layout, renders a "See details" CTA after the
   * remaining-to-allocate copy.
   */
  onSeeDetails?: () => void
  /**
   * When true in the inline layout, renders a dismiss button that hides the
   * banner via local state.
   */
  dismissible?: boolean
  className?: string
}

export function AllocationNotify({
  effectiveBudget,
  ineffectiveBudget,
  unallocatedBudget,
  inverse = false,
  layout = 'panel',
  onSeeDetails,
  dismissible = false,
  className,
}: AllocationNotifyProps) {
  const [isDismissed, setIsDismissed] = useState(false)

  const { isFullyAllocated, neverAllocatedAmount, hasUnderDelivery, showAllocationWarning, underDeliveredAmount } =
    getAllocationChecks({
      effectiveBudget,
      ineffectiveBudget,
      unallocatedBudget,
    })

  const bgColor = inverse ? 'bg-sub-surface-bg-inverse' : 'bg-sub-surface-bg'
  const textColor = inverse ? 'text-header-inverse' : 'text-header'

  if (layout === 'inline') {
    if (!showAllocationWarning || isDismissed) return null

    return (
      <div className={cn('flex items-center justify-between gap-4 px-6 py-4', bgColor, className)}>
        <div className="flex items-center gap-2 min-w-0">
          <Icon name="TriangleAlert" className="size-4 text-warning shrink-0" />
          <div className="flex items-center gap-1 flex-wrap">
            <Text variant="sm-sb" inline className="text-warning">
              Allocation Needed:
            </Text>
            <Text variant="base-sm" inline className={textColor}>
              {formatMoney(unallocatedBudget)} remaining to allocate.
            </Text>
            {onSeeDetails && (
              <Button type="button" variant="text" size="inline" onClick={onSeeDetails}>
                <Text variant="base-sm" inline className="h-auto text-primary-accent underline bg-transparent">
                  See details
                </Text>
              </Button>
            )}
          </div>
        </div>
        {dismissible && (
          <button
            type="button"
            onClick={() => setIsDismissed(true)}
            className={cn('shrink-0 hover:text-header transition-colors', textColor)}
            aria-label="Dismiss"
          >
            <Icon name="X" className={cn('size-4', textColor)} />
          </button>
        )}
      </div>
    )
  }

  if (isFullyAllocated) {
    return (
      <div className={cn('flex items-center justify-between gap-2 rounded p-4', bgColor, className)}>
        <Text variant="base-sb" className={textColor}>
          Budget Fully Allocated
        </Text>
        <AllocationStatusBadge status="allocated" />
      </div>
    )
  }

  if (!showAllocationWarning) return null

  return (
    <div className={cn('flex flex-col gap-[6px] rounded p-4', bgColor, className)}>
      <div className="flex items-start justify-between gap-2">
        <Text variant="base-sb" className={textColor}>
          Allocation Needed:
        </Text>
        <AllocationStatusBadge status="warning" />
      </div>

      <AllocationBreakdown
        unallocatedBudget={unallocatedBudget}
        underDeliveredAmount={underDeliveredAmount}
        neverAllocatedAmount={neverAllocatedAmount}
        hasUnderDelivery={hasUnderDelivery}
        textClassName={textColor}
      />

      <Text variant="base-sm" className={cn(textColor, 'mt-4')}>
        {hasUnderDelivery
          ? 'Add a new flight or create a new campaign to spend your budget in full.'
          : 'Add or adjust flights to spend your budget in full or create another campaign.'}
      </Text>
    </div>
  )
}
